import React, {
  useState,
  ChangeEvent,
  FocusEvent,
  FormEvent,
  useEffect,
} from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Divider,
  Paper,
  Link,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import { Visibility, VisibilityOff, Google } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import RestorePasswordModal from "./components/RestorePasswordModal";
import { LOGIN_Feature } from "@/features.config";
import { AuthAlerts } from "./components/Alerts";

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  email: string;
  password: string;
}

interface TouchedFields {
  email: boolean;
  password: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`login-tabpanel-${index}`}
      aria-labelledby={`login-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const LoginForm: React.FC = () => {
  const { login, loginWithGoogle, loginAttendantWithGoogle, resetPasswordRequest, isLoading, currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({
    email: "",
    password: "",
  });
  const [touched, setTouched] = useState<TouchedFields>({
    email: false,
    password: false,
  });

  // Estados para las alertas
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<"error" | "info" | "success">("success");
  const [alertMessage, setAlertMessage] = useState("");


  useEffect(() => {
    if (currentUser && isAuthenticated) {
      // Delay navigation to allow alert to show
      setTimeout(() => {
        navigate("/");
      }, 1500);
    }
  }, [currentUser]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const validateEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" && "checked" in e.target
        ? e.target.checked
        : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? Boolean(checked) : value,
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === "email" && formData.email && !validateEmail(formData.email)) {
      setErrors((prev) => ({ ...prev, email: "Ingrese un email válido" }));
    }

    if (
      name === "password" &&
      formData.password &&
      formData.password.length < 6
    ) {
      setErrors((prev) => ({
        ...prev,
        password: "La contraseña debe tener al menos 6 caracteres",
      }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors: Partial<FormErrors> = {};

    if (!formData.email) {
      newErrors.email = "El email es requerido";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Ingrese un email válido";
    }

    if (!formData.password) {
      newErrors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors as FormErrors);
      setTouched({ email: true, password: true });
      return;
    }

    try {
      const result = await login(formData.email, formData.password);
      if (result && result.displayName) {
        setAlertSeverity("success");
        setAlertMessage(`¡Bienvenido/a ${result.displayName}!`);
        setAlertOpen(true);
      }
    } catch (error) {
      setAlertSeverity("error");
      setAlertMessage("Error al iniciar sesión. Por favor, verifica tus credenciales.");
      setAlertOpen(true);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await loginWithGoogle();
      if (result && result.displayName) {
        setAlertSeverity("success");
        setAlertMessage(`¡Bienvenido/a ${result.displayName}!`);
        setAlertOpen(true);
      }
    } catch (error) {
      console.error("Error signing in with Google:", error);
      setAlertSeverity("error");
      setAlertMessage(`Error al iniciar sesión con Google: ${error}`);
      setAlertOpen(true);
    }
  };

  const handleAttendantGoogleSignIn = async () => {
    try {
      const result = await loginAttendantWithGoogle();
      if (result && result.displayName) {
        setAlertSeverity("success");
        setAlertMessage(`¡Bienvenido/a ${result.displayName}!`);
        setAlertOpen(true);
      }
    } catch (error) {
      console.error("Error signing in attendant with Google:", error);
      setAlertSeverity("error");
      setAlertMessage("Error al iniciar sesión con Google. Por favor, intenta nuevamente.");
      setAlertOpen(true);
    }
  };

  const handleForgotPasswordOpen = () => {
    setForgotPasswordOpen(true);
  };

  const handleForgotPasswordClose = () => {
    setForgotPasswordOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        backgroundColor: "#f7f7f7",
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          alignItems: "start",
          justifyContent: "start",
          gap: { xs: 0, md: 4 },
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "45%" },
            height: { xs: "200px", sm: "250px", md: "90vh" },
            display: { xs: "flex", md: "flex" },
            justifyItems: "start",
            mb: { xs: 2, md: 0 },
          }}
        >
          <Box
            component="img"
            src="images/img3.jpg"
            alt="login illustration"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 2,
              objectFit: "cover",
            }}
          />
        </Box>
        <Paper
          elevation={1}
          sx={{
            width: { xs: "100%", md: "55%" },
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
            height: "100%",
            minHeight: { xs: "auto", md: "90vh" },
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, pb: 2, textAlign: "center" }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" } }}
            >
              Iniciar Sesión
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
            >
              ¡Bienvenido de nuevo! Inicie sesión para acceder a su cuenta.
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 2, sm: 3, md: 4 } }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="login tabs"
              centered
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.95rem" },
                  minWidth: { xs: "auto", sm: "120px" },
                  px: { xs: 1, sm: 2 },
                },
              }}
            >
              <Tab label="Acceso Intitucional" id="login-tab-0" />
              {LOGIN_Feature && LOGIN_Feature === 'true' && <Tab label="Acudientes" id="login-tab-1" />}
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box
              sx={{
                px: { xs: 2, sm: 3, md: 4 },
                pb: { xs: 3, sm: 4 },
                pt: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: { xs: "300px", sm: "350px", md: "400px" },
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                gutterBottom
                sx={{
                  mb: 2,
                  textAlign: "center",
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                }}
              >
                Acceso Institucional
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 4,
                  textAlign: "center",
                  maxWidth: { xs: "100%", sm: "350px", md: "400px" },
                  fontSize: { xs: "0.813rem", sm: "0.875rem" },
                  px: { xs: 1, sm: 0 },
                }}
              >
                Docentes, administrativos y coordinadores deben iniciar sesión con
                su cuenta de Google institucional
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                disabled={isLoading}
                onClick={handleGoogleSignIn}
                startIcon={<Google />}
                sx={{
                  py: { xs: 1.2, sm: 1.5 },
                  borderRadius: "8px",
                  borderColor: "#4285f4",
                  backgroundColor: "#fff",
                  color: "#4285f4",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                  maxWidth: { xs: "100%", sm: "350px", md: "400px" },
                  "&:hover": {
                    backgroundColor: "#f8fbff",
                    borderColor: "#3367d6",
                  },
                  "&:disabled": {
                    backgroundColor: "#fafafa",
                    color: "text.disabled",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: "#4285f4" }} />
                ) : (
                  "Continuar con Google"
                )}
              </Button>

              <Box
                sx={{
                  mt: { xs: 3, sm: 4 },
                  p: { xs: 1.5, sm: 2 },
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  maxWidth: { xs: "100%", sm: "350px", md: "400px" },
                  width: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    fontSize: { xs: "0.75rem", sm: "0.813rem" },
                  }}
                >
                  Si eres acudiente o padre de familia, por favor utiliza la
                  pestaña "Acudientes" para iniciar sesión o registrarte
                </Typography>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                px: { xs: 2, sm: 4, md: 8 },
                pb: { xs: 3, sm: 4 },
                pt: 3,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f5f5f5",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#a8a8a8",
                  },
                },
              }}
            >
              {/* Email */}
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{
                  mb: 0.5,
                  color: "text.primary",
                  fontSize: { xs: "0.813rem", sm: "0.875rem" },
                }}
              >
                Correo Electrónico
              </Typography>

              <TextField
                fullWidth
                name="email"
                placeholder="Ingrese email"
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                disabled={isLoading}
                InputProps={{
                  sx: {
                    backgroundColor: "#fafafa",
                    mb: errors.email && touched.email ? 1 : 2,
                    borderRadius: "8px",
                    "& fieldset": { border: "none" },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />

              {/* Password */}
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{
                  mb: 0.5,
                  color: "text.primary",
                  fontSize: { xs: "0.813rem", sm: "0.875rem" },
                }}
              >
                Contraseña
              </Typography>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Ingrese contraseña"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                disabled={isLoading}
                InputProps={{
                  sx: {
                    backgroundColor: "#fafafa",
                    borderRadius: "8px",
                    "& fieldset": { border: "none" },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        aria-label={
                          showPassword
                            ? "Ocultar contraseña"
                            : "Mostrar contraseña"
                        }
                        disabled={isLoading}
                        size={window.innerWidth < 600 ? "small" : "medium"}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
              >
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      color="primary"
                      disabled={isLoading}
                      sx={{
                        color: "text.secondary",
                        "&.Mui-checked": { color: "grey.600" },
                      }}
                    />
                  }
                  label="Remember me"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    marginTop: 1,
                  }}
                /> */}
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  color="grey"
                  type="button"
                  disabled={isLoading}
                  onClick={handleForgotPasswordOpen}
                  sx={{ fontSize: { xs: "0.813rem", sm: "0.875rem" } }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Box>

              {/* Submit Button */}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: { xs: 2, sm: 3 },
                  mb: 2,
                  py: { xs: 1, sm: 1.2 },
                  backgroundColor: "secondary.main",
                  "&:hover": { backgroundColor: "secondary.500" },
                  "&:disabled": { backgroundColor: "#d4d4d4" },
                  color: "#fff",
                  position: "relative",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{
                        color: "#fff",
                        position: "absolute",
                        left: "50%",
                        marginLeft: "-12px",
                      }}
                    />
                    <span style={{ opacity: 0 }}>Entrar</span>
                  </>
                ) : (
                  "Entrar"
                )}
              </Button>

              <Divider sx={{ color: "grey", my: { xs: 2, sm: 3 }, fontSize: { xs: "0.813rem", sm: "0.875rem" } }}>O</Divider>

              <Button
                fullWidth
                variant="outlined"
                disabled={isLoading}
                onClick={handleAttendantGoogleSignIn}
                startIcon={<Google />}
                sx={{
                  py: { xs: 1, sm: 1.2 },
                  borderRadius: "8px",
                  borderColor: "#4285f4",
                  backgroundColor: "#fff",
                  color: "#4285f4",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.813rem", sm: "0.875rem", md: "0.95rem" },
                  "&:hover": {
                    backgroundColor: "#f8fbff",
                    borderColor: "#3367d6",
                  },
                  "&:disabled": {
                    backgroundColor: "#fafafa",
                    color: "text.disabled",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: "#4285f4" }} />
                ) : (
                  "Continuar con Google"
                )}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                disabled={isLoading}
                sx={{
                  mt: 2,
                  py: { xs: 1, sm: 1.2 },
                  borderRadius: "8px",
                  borderColor: "#eee",
                  backgroundColor: "#fafafa",
                  color: "text.primary",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.813rem", sm: "0.875rem", md: "0.95rem" },
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    borderColor: "#ddd",
                  },
                  "&:disabled": {
                    backgroundColor: "#fafafa",
                    color: "text.disabled",
                  },
                }}
                onClick={() => navigate("/register")}
              >
                Registro de Acudiente
              </Button>
            </Box>
          </TabPanel>


        </Paper>
      </Container>

      {/* Forgot Password Modal */}
      <RestorePasswordModal
        open={forgotPasswordOpen}
        onClose={handleForgotPasswordClose}
        initialEmail={formData.email}
        resetPasswordRequest={resetPasswordRequest}
      />

      {/* Alertas de inicio de sesión */}
      <AuthAlerts
        alertOpen={alertOpen}
        setAlertOpen={setAlertOpen}
        alertSeverity={alertSeverity}
        alertMessage={alertMessage}
      />
    </Box>
  );
};

export default LoginForm;
